import { Injectable } from '@angular/core';

export interface BrowserInfo {
  name: string;
  fullVersion: string;
  source: 'navigator.userAgentData' | 'navigator.userAgent';
  // Properties specific to userAgentData
  mobile?: boolean;
  platform?: string;
  // Properties specific to userAgent
  userAgent?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BrowserDetectorService {

  constructor() { }

  /**
   * Detects browser information using navigator.userAgentData if available,
   * otherwise falls back to parsing navigator.userAgent.
   * @returns BrowserInfo object containing browser details.
   */
  getBrowserAndVersion(): BrowserInfo {
    // --- Modern Approach: navigator.userAgentData ---
    if (navigator['userAgentData']) {
      const uaData = navigator['userAgentData'];
      const brands = uaData.brands;
      let browserName = undefined;
      let browserVersion = undefined;

      if (brands && brands.length > 0) {
        // 1. Filter out known "junk" brands like "Not)A;Brand" and generic "Chromium"
        const filteredBrands = brands.filter(brand =>
          brand.brand !== "Not)A;Brand" &&
          brand.brand !== "Chromium"
        );

        // 2. Prioritize well-known actual browser names
        // Check for Google Chrome first, as it's often present and clearly identifiable
        let actualBrowserBrand = filteredBrands.find(brand => brand.brand === "Google Chrome");

        // If not Google Chrome, check for other distinct browser names like "Microsoft Edge", "Firefox", "Safari", "Opera"
        if (!actualBrowserBrand) {
          actualBrowserBrand = filteredBrands.find(brand =>
            brand.brand.includes("Edge") || // Microsoft Edge
            brand.brand.includes("Firefox") || // Mozilla Firefox
            brand.brand.includes("Safari") || // Apple Safari
            brand.brand.includes("Opera") // Opera
          );
        }

        // 3. If a clear browser is found, use its details
        if (actualBrowserBrand) {
          browserName = actualBrowserBrand.brand;
          browserVersion = actualBrowserBrand.version;

          // Normalize common names
          if (browserName === "Google Chrome") {
            browserName = "Google Chrome";
          } else if (browserName === "Microsoft Edge") {
            browserName = "Microsoft Edge";
          } else if (browserName.includes("Firefox")) { // Handle "Mozilla Firefox" etc.
            browserName = "Mozilla Firefox";
          } else if (browserName.includes("Safari")) { // Handle "Apple Safari" etc.
            browserName = "Safari";
          }
        } else if (filteredBrands.length > 0) {
          // 4. As a last resort, if "Not)A;Brand" and "Chromium" are filtered out,
          // and no specific browser was identified, take the first remaining brand.
          // This might still be a less specific name, but better than "Not)A;Brand".
          browserName = filteredBrands[0].brand;
          browserVersion = filteredBrands[0].version;
        }
      }

      return {
        name: browserName,
        fullVersion: browserVersion,
        mobile: uaData.mobile,
        platform: uaData.platform,
        source: "navigator.userAgentData"
      };
    }

    // --- Fallback Approach: navigator.userAgent ---
    else {
      const userAgent = navigator.userAgent;
      let browserName = undefined;
      let platform = undefined;
      let fullVersion = undefined;

      // Order matters here, as some user agents contain strings for multiple browsers (e.g., Chrome has "Safari")

      if (userAgent.indexOf("Opera") !== -1 || userAgent.indexOf("Opr") !== -1) {
        browserName = "Opera";
        let match = userAgent.match(/(Opera|Opr)\/(\d+\.\d+)/);
        if (match) {
          fullVersion = match[2];
        }
      } else if (userAgent.indexOf("Edge") !== -1) {
        // Edge (Chromium-based)
        browserName = "Microsoft Edge";
        let match = userAgent.match(/Edge\/(\d+\.\d+)/);
        if (match) {
          fullVersion = match[1];
        }
      } else if (userAgent.indexOf("Chrome") !== -1) {
        browserName = "Google Chrome";
        let match = userAgent.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/);
        if (match) {
          fullVersion = match[1];
        }
      } else if (userAgent.indexOf("Safari") !== -1) {
        browserName = "Safari";
        let match = userAgent.match(/Version\/(\d+\.\d+)/);
        if (match) {
          fullVersion = match[1];
        }
      } else if (userAgent.indexOf("Firefox") !== -1) {
        browserName = "Mozilla Firefox";
        let match = userAgent.match(/Firefox\/(\d+\.\d+)/);
        if (match) {
          fullVersion = match[1];
        }
      } else if (userAgent.indexOf("MSIE") !== -1 || userAgent.indexOf("Trident") !== -1) {
        browserName = "Internet Explorer";
        if (userAgent.indexOf("MSIE") !== -1) {
          let match = userAgent.match(/MSIE (\d+\.\d+)/);
          if (match) {
            fullVersion = match[1];
          }
        } else { // IE 11 and later use "Trident"
          let match = userAgent.match(/rv:(\d+\.\d+)/);
          if (match) {
            fullVersion = match[1];
          }
        }
      }

      if (userAgent.includes("Windows")) {
        platform = "Windows";
      } else if (userAgent.includes("Mac OS")) {
        platform = "Mac OS";
      } else if (userAgent.includes("Linux")) {
        platform = "Linux";
      } else if (userAgent.includes("Android")) {
        platform = "Android";
      } else if (userAgent.includes("iPhone") || userAgent.includes("iPad") || userAgent.includes("iPod")) {
        platform = "iOS";
      }

      return {
        name: browserName,
        fullVersion: fullVersion,
        platform: platform,
        userAgent: userAgent,
        source: "navigator.userAgent"
      };
    }
  }
}
